import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { usePageTracking } from "@/hooks/usePageTracking";
import { initGA } from "@/lib/analytics";
import { lazy, Suspense, useEffect } from "react";
import { ScrollToTop } from "./components/ScrollToTop";
import { initializeMarketingAttribution } from "@/lib/marketingAttribution";
import { AuthProvider } from "@/contexts/AuthContext";

// Critical path components - load immediately
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import ShortLinkRedirect from "./pages/ShortLinkRedirect";

// Lazy load all other pages to reduce initial bundle size
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const Terms = lazy(() => import("./pages/legal/Terms"));
const ServiceAgreement = lazy(() => import("./pages/legal/ServiceAgreement"));
const CCPARequest = lazy(() => import("./pages/legal/CCPARequest"));
const VerifyCCPA = lazy(() => import("./pages/legal/VerifyCCPA"));
const PoolCleaning = lazy(() => import("./pages/services/PoolCleaning"));
const PoolMaintenance = lazy(() => import("./pages/services/PoolMaintenance"));
const ChemicalBalancing = lazy(() => import("./pages/services/ChemicalBalancing"));
const EquipmentRepair = lazy(() => import("./pages/services/EquipmentRepair"));
const SeasonalServices = lazy(() => import("./pages/services/SeasonalServices"));
const PoolInspection = lazy(() => import("./pages/services/PoolInspection"));
const EmergencyService = lazy(() => import("./pages/services/EmergencyService"));

// Service Areas
const ServiceAreas = lazy(() => import("./pages/ServiceAreas"));
const RanchoSantaFe = lazy(() => import("./pages/service-areas/RanchoSantaFe"));
const FairbanksRanch = lazy(() => import("./pages/service-areas/FairbanksRanch"));
const Santaluz = lazy(() => import("./pages/service-areas/Santaluz"));
const TheCrosby = lazy(() => import("./pages/service-areas/TheCrosby"));
const DelMar = lazy(() => import("./pages/service-areas/DelMar"));
const SolanaBeach = lazy(() => import("./pages/service-areas/SolanaBeach"));
const CarmelValley = lazy(() => import("./pages/service-areas/CarmelValley"));
const LaJolla = lazy(() => import("./pages/service-areas/LaJolla"));
const Encinitas = lazy(() => import("./pages/service-areas/Encinitas"));
const TorreyHighlands = lazy(() => import("./pages/service-areas/TorreyHighlands"));
const DelSur = lazy(() => import("./pages/service-areas/DelSur"));
const FourSRanch = lazy(() => import("./pages/service-areas/FourSRanch"));
const CardiffByTheSea = lazy(() => import("./pages/service-areas/CardiffByTheSea"));
const Olivenhain = lazy(() => import("./pages/service-areas/Olivenhain"));
const LaCosta = lazy(() => import("./pages/service-areas/LaCosta"));
const Poway = lazy(() => import("./pages/service-areas/Poway"));
const ScrippsRanch = lazy(() => import("./pages/service-areas/ScrippsRanch"));
const RanchoBernardo = lazy(() => import("./pages/service-areas/RanchoBernardo"));

// Resources
const PoolCareTips = lazy(() => import("./pages/resource/PoolCareTips"));
const PoolMaintenanceGuide = lazy(() => import("./pages/resource/PoolMaintenanceGuide"));
const SeasonalPoolCare = lazy(() => import("./pages/resource/SeasonalPoolCare"));
const Pricing = lazy(() => import("./pages/Pricing"));

// Work With Us Pages
const Jobs = lazy(() => import("./pages/work-with-us/Jobs"));
const SellYourRoute = lazy(() => import("./pages/work-with-us/SellYourRoute"));
const Marketplace = lazy(() => import("./pages/work-with-us/Marketplace"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const CPOCertification = lazy(() => import("./pages/certifications/CPOCertification"));
const Legal = lazy(() => import("./pages/Legal"));
const QRHelp = lazy(() => import("./pages/QRHelp"));
const DoorHangerPromo = lazy(() => import("./pages/DoorHangerPromo"));
const NextdoorPromo1 = lazy(() => import("./pages/promo/NextdoorPromo1"));
const RalphsEncinitas = lazy(() => import("./pages/promo/RalphsEncinitas"));
const ReferAFriend = lazy(() => import("./pages/ReferAFriend"));
const HealthLovable = lazy(() => import("./pages/HealthLovable"));
const Pay = lazy(() => import("./pages/Pay"));

const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const ShortenLinks = lazy(() => import("./pages/admin/ShortenLinks"));
const BlogEditor = lazy(() => import("./pages/admin/BlogEditor"));
const AdminLogin = lazy(() => import("./pages/admin/Login"));
const AdminVerify = lazy(() => import("./pages/admin/Verify"));
const AdminResetPassword = lazy(() => import("./pages/admin/ResetPassword"));
const AdminLogout = lazy(() => import("./pages/admin/Logout"));
const AdminVerifyRequired = lazy(() => import("./pages/admin/VerifyRequired"));
const AdminUnauthorized = lazy(() => import("./pages/admin/Unauthorized"));

// Route guards - imported directly (not lazy) so they gate access immediately
import ProtectedRoute from "./components/admin/ProtectedRoute";
import AdminRoute from "./components/admin/AdminRoute";

const queryClient = new QueryClient();

// Component to handle page tracking
const AppWithTracking = () => {
  usePageTracking();
  
  useEffect(() => {
    // Initialize Google Analytics
    initGA();
    
    // Initialize marketing attribution tracking
    initializeMarketingAttribution();
  }, []);
  
  return null;
};

// Loading fallback component
const PageFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="text-muted-foreground">Loading...</div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Sonner />
      <BrowserRouter>
        <AppWithTracking />
        <ScrollToTop />
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/pool-cleaning" element={<PoolCleaning />} />
            <Route path="/pool-maintenance" element={<PoolMaintenance />} />
            <Route path="/pool-chemical-balancing" element={<ChemicalBalancing />} />
            <Route path="/pool-equipment-repair" element={<EquipmentRepair />} />
            <Route path="/pool-opening-closing" element={<SeasonalServices />} />
            <Route path="/pool-inspection" element={<PoolInspection />} />
            <Route path="/emergency-pool-service" element={<EmergencyService />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/cpo-certification" element={<CPOCertification />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<Terms />} />
            <Route path="/service-agreement" element={<ServiceAgreement />} />
            <Route path="/legal/ccpa-request" element={<CCPARequest />} />
            <Route path="/verify-ccpa" element={<VerifyCCPA />} />
            <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/legal/terms" element={<Terms />} />
            <Route path="/legal/service-agreement" element={<ServiceAgreement />} />
            {/* Service Area Routes */}
            <Route path="/service-areas" element={<ServiceAreas />} />
            <Route path="/service-areas/rancho-santa-fe" element={<RanchoSantaFe />} />
            <Route path="/service-areas/fairbanks-ranch" element={<FairbanksRanch />} />
            <Route path="/service-areas/santaluz" element={<Santaluz />} />
            <Route path="/service-areas/the-crosby-at-rancho-santa-fe" element={<TheCrosby />} />
            <Route path="/service-areas/del-mar" element={<DelMar />} />
            <Route path="/service-areas/solana-beach" element={<SolanaBeach />} />
            <Route path="/service-areas/carmel-valley" element={<CarmelValley />} />
            <Route path="/service-areas/la-jolla" element={<LaJolla />} />
            <Route path="/service-areas/encinitas" element={<Encinitas />} />
            <Route path="/service-areas/torrey-highlands" element={<TorreyHighlands />} />
            <Route path="/service-areas/del-sur" element={<DelSur />} />
            <Route path="/service-areas/4s-ranch" element={<FourSRanch />} />
            <Route path="/service-areas/cardiff-by-the-sea" element={<CardiffByTheSea />} />
            <Route path="/service-areas/olivenhain" element={<Olivenhain />} />
            <Route path="/service-areas/la-costa" element={<LaCosta />} />
            <Route path="/service-areas/poway" element={<Poway />} />
            <Route path="/service-areas/scripps-ranch" element={<ScrippsRanch />} />
            <Route path="/service-areas/rancho-bernardo" element={<RanchoBernardo />} />
            {/* Resource Routes */}
            <Route path="/pool-care-tips" element={<PoolCareTips />} />
            <Route path="/pool-maintenance-guide" element={<PoolMaintenanceGuide />} />
            <Route path="/seasonal-pool-care" element={<SeasonalPoolCare />} />
            {/* Work With Us Routes */}
            <Route path="/jobs" element={<Jobs />} />
            <Route path="/sell-your-route" element={<SellYourRoute />} />
            <Route path="/marketplace" element={<Marketplace />} />
            {/* Legal Routes */}
            <Route path="/legal" element={<Legal />} />
            {/* QR Code Routes */}
            <Route path="/qr/help" element={<QRHelp />} />
            <Route path="/qr/door1" element={<DoorHangerPromo />} />
            <Route path="/qr/ralphs-encinitas" element={<RalphsEncinitas />} />
            {/* Promo Routes */}
            <Route path="/promo/nextdoor/1" element={<NextdoorPromo1 />} />
            <Route path="/refer" element={<ReferAFriend />} />
            {/* Payment Routes */}
            <Route path="/pay" element={<Pay />} />
            {/* Short Link Routes */}
            <Route path="/s/:code" element={<ShortLinkRedirect />} />
            {/* Admin Auth Routes (public) */}
            <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/verify" element={<AdminVerify />} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />
            <Route path="/admin/logout" element={<AdminLogout />} />
            <Route path="/admin/verify-required" element={<AdminVerifyRequired />} />
            <Route path="/admin/unauthorized" element={<AdminUnauthorized />} />
            {/* Admin Protected Routes */}
            <Route path="/admin/shorten" element={<AdminRoute><ShortenLinks /></AdminRoute>} />
            <Route path="/admin/blog" element={<AdminRoute><BlogEditor /></AdminRoute>} />
            {/* Blog Routes */}
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            {/* Health Check Routes */}
            <Route path="/health-blx-site-lovable" element={<HealthLovable />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
